import { type ReactNode } from "react";
import { Container } from "./Container";

interface StatItem {
  /** Big number, e.g. "10 000" */
  value: string;
  /** Accent suffix, e.g. "+" — styled in the current brand color */
  accent?: string;
  /** Optional icon instead of accent */
  icon?: ReactNode;
  /** Description below the number */
  label: string;
}

interface StatsBarProps {
  id?: string;
  className?: string;
  items: StatItem[];
}

export function StatsBar({ id, className = "", items }: StatsBarProps) {
  return (
    <section id={id} className={`flex flex-col items-start py-[80px] ${className}`}>
      {/* Full-bleed gradient band stays edge-to-edge; stats content aligns to the grid */}
      <div
        className="relative w-full pt-[40px] pb-[80px]"
        style={{ background: "linear-gradient(180deg, var(--brand-primary-soft) 0%, var(--brand-primary-tint) 62%, #FFFFFF 100%)" }}
      >
      {/* desktop ≥1269 — 4-in-row flex; tablet 769–1268 — 4-in-row scaled;
          mobile <769 — 2×2 grid */}
      <Container className="flex items-start justify-between gap-[16px] max-desktop:gap-[16px] max-tablet:grid max-tablet:grid-cols-2 max-tablet:gap-x-[16px] max-tablet:gap-y-[32px]">
        {items.map((item, i) => (
          <div
            key={i}
            className="flex flex-col items-center text-center flex-1 min-w-0 max-desktop:w-auto max-desktop:flex-1 max-desktop:min-w-0 max-tablet:w-full"
          >
            {/* Number row */}
            <div className="flex items-center gap-[4px] px-[16px] py-[24px] rounded-[40px] shrink-0 max-desktop:px-0 max-desktop:py-[12px]">
              <span
                className="[font-family:var(--font-commissioner)] font-bold not-italic text-[64px] leading-[64px] text-[rgba(0,0,0,0.9)] tracking-[-1.28px] whitespace-nowrap max-desktop:text-[32px] max-desktop:leading-[40px] max-desktop:tracking-[-0.64px]"
                style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
              >
                {item.value}
              </span>
              {item.accent && (
                <span
                  className="[font-family:var(--font-commissioner)] font-bold not-italic text-[64px] leading-[64px] text-[var(--brand-primary)] tracking-[-1.28px] whitespace-nowrap max-desktop:text-[32px] max-desktop:leading-[40px] max-desktop:tracking-[-0.64px]"
                  style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
                >
                  {item.accent}
                </span>
              )}
              {item.icon && (
                <span className="flex items-center justify-center shrink-0 h-[53px] w-[52px] max-desktop:h-[32px] max-desktop:w-[32px] [&>svg]:h-full [&>svg]:w-full">
                  {item.icon}
                </span>
              )}
            </div>
            {/* Label */}
            <p
              className="[font-family:var(--font-commissioner)] font-medium not-italic text-[18px] leading-[24px] text-[rgba(0,0,0,0.9)] tracking-[-0.36px] w-full text-balance max-desktop:text-[16px] max-desktop:leading-[20px] max-desktop:tracking-[-0.32px]"
              style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
            >
              {item.label}
            </p>
          </div>
        ))}
      </Container>
      </div>
    </section>
  );
}
